import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import moment from "moment";
import "moment-timezone";

import {
    setLoading as setFlightBannersLoading,
    setFeedbacks as setFlightBanners,
    setError,
} from "../../features/common/feedbackSlice";

import { deleteFeedbackById, deleteFlightBannerById, getFlightBannersList } from "../../services/userservice";

const FlightBannersList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const { feedbacks: flightBanners, loading, error } = useSelector(
        (state) => state?.feedback
    );
    const [showModal, setShowModal] = useState(false);
    const [selectedBannerId, setSelectedBannerId] = useState(null);
    const [deleteBanner, setDeleteBanner] = useState("");
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchFlightBannerListData = async () => {
            try {
                dispatch(setFlightBannersLoading());
                const response = await getFlightBannersList();
                dispatch(setFlightBanners(response));
            } catch (error) {
                dispatch(setError(error.message));
            }
        };
        fetchFlightBannerListData();
    }, [dispatch, deleteBanner]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Filter banners based on search term
    const filteredFlightBanners = (flightBanners || []).filter(
        (banner) =>
            banner?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            banner?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            banner?.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            banner?.holiday_destination?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            banner?.packageCategories?.some((cat) =>
                cat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            )
    );

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const handleDelete = async () => {
        try {
            const deleteResponse = await deleteFlightBannerById(selectedBannerId);
            if (deleteResponse.status === 200 || deleteResponse.status === 201) {
                setDeleteBanner(selectedBannerId);
                closeModal();
            } else {
                setError(deleteResponse.message || "Failed to delete ");
            }
        } catch (error) {
            alert("You are not authorized to delete this banner");
        }
    };

    const openModal = (bannerId) => {
        setSelectedBannerId(bannerId);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const confirmDelete = async () => {
        await handleDelete(selectedBannerId);
        closeModal();
    };

    // Pagination
    const indexOfLastBanner = currentPage * rowsPerPage;
    const indexOfFirstBanner = indexOfLastBanner - rowsPerPage;
    const bannerLists = filteredFlightBanners.slice(
        indexOfFirstBanner,
        indexOfLastBanner
    );

    const totalPages = Math.ceil(filteredFlightBanners.length / rowsPerPage);

    const renderPageNumbers = () => {
        const pages = [];
        if (totalPages <= 10) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1, 2, 3);
            if (currentPage > 5) pages.push("...");
            const start = Math.max(4, currentPage - 1);
            const end = Math.min(totalPages - 3, currentPage + 1);
            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 4) pages.push("...");
            pages.push(totalPages - 2, totalPages - 1, totalPages);
        }

        return pages.map((page, index) => (
            <li
                key={index}
                className={`page-item ${page === currentPage ? "active" : ""
                    } ${page === "..." ? "disabled" : ""}`}
            >
                {page === "..." ? (
                    <span className="page-link">...</span>
                ) : (
                    <button className="page-link" onClick={() => setCurrentPage(page)}>
                        {page}
                    </button>
                )}
            </li>
        ));
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">Flight Banners</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item">
                                    <Link to="/dashboard">Home</Link>
                                </li>
                                <li className="breadcrumb-item active">Flight Banners</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card">
                                {/* Header */}
                                <div className="card-header d-md-flex align-items-center justify-content-end">
                                    <Link
                                        to="/add-flight-banner"
                                        className="btn btn-primary mb-md-0 mb-2 py-1 mr-3"
                                    >
                                        <i className="fas fa-plus" /> Add Flight Banner
                                    </Link>
                                    <div className="card-tools">
                                        <div
                                            className="input-group input-group-sm"
                                            style={{ width: "250px" }}
                                        >
                                            <input
                                                type="text"
                                                name="table_search"
                                                className="form-control float-right"
                                                placeholder="Search"
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                            />
                                            <div className="input-group-append">
                                                <button type="submit" className="btn btn-default">
                                                    <i className="fas fa-search" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="card-body">
                                    {/* Table */}
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Title</th>
                                                    <th>Banner</th>
                                                    <th>Status</th>
                                                    <th>Created At</th>
                                                    <th>Updated At</th>
                                                    <th style={{ width: "15%" }}>Action</th>
                                                </tr>
                                            </thead>
                                            {bannerLists.length > 0 ? (
                                                <tbody>
                                                    {bannerLists.map((banner) => (
                                                        <tr key={banner.id}>
                                                            <td>{banner.id}</td>
                                                            <td>{banner.title}</td>
                                                            <td>
                                                                <img
                                                                    src={banner?.banner_url + banner?.banner_image || banner?.banner_image  }
                                                                    alt={banner.title}
                                                                    style={{ width: "120px", height: "60px", objectFit: "cover" }}
                                                                />
                                                            </td>

                                                            <td>
                                                                <span
                                                                    className={`badge p-2 w-100 ${banner?.is_active ? "badge-success" : "badge-danger"}`}>
                                                                    {banner?.is_active ? "ACTIVE" : "INACTIVE"}
                                                                </span>
                                                            </td>
                                                            <td>{moment(banner.created_at).format("DD-MM-YYYY hh:mm A")}</td>
                                                            <td>{moment(banner.updated_at).format("DD-MM-YYYY hh:mm A")}</td>

                                                            <td>
                                                                <div
                                                                    className="d-flex align-items-center"
                                                                    style={{ gap: "10px" }}
                                                                >
                                                                    <Link to={`/edit-flight-banner/${banner.id}`}>
                                                                        <button className="btn btn-primary btn-sm">Edit</button>
                                                                    </Link>

                                                                    <button
                                                                        className="btn btn-danger btn-sm"
                                                                        onClick={() => openModal(banner?.id)}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr>
                                                        <td colSpan="8" className="text-center">
                                                            No Flight Banners found
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )}
                                        </table>
                                    </div>


                                    {/* Pagination */}
                                    <div className="d-md-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {indexOfFirstBanner + 1} to{" "}
                                            {Math.min(indexOfLastBanner, filteredFlightBanners.length)}{" "}
                                            of {filteredFlightBanners.length} entries
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li
                                                    className={`page-item ${currentPage === 1 ? "disabled" : ""
                                                        }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage - 1)}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>

                                                {renderPageNumbers()}

                                                <li
                                                    className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                                        }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(currentPage + 1)}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Delete Modal */}
            <Modal show={showModal} onHide={closeModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center p-4">
                        <h5>Are you sure you want to delete this Flight Banner?</h5>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={closeModal}>
                        Cancel
                    </button>
                    <button className="btn btn-danger" onClick={confirmDelete}>
                        OK
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default FlightBannersList;
